import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, iif, map, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, startWith, switchMap, take } from 'rxjs/operators';
import { User } from '../models';
import { StudentList } from '../models/StudentList';
import { FlexPeriodService, FlexVisibility } from './flex-period.service';
import { SchoolActivityService } from './school-activity.service';
import { UserService } from './user.service';

export type FlexVisibilityTooltipMessages = {
	addedByMsg?: string;
	visibilityMsg?: string;
};

@Injectable({
	providedIn: 'root',
})
export class FlexVisibilityService {
	constructor(private schoolActivityService: SchoolActivityService, private flexPeriodService: FlexPeriodService, public userService: UserService) {}

	existingSelectedFlexGroups$: BehaviorSubject<StudentList[]> = new BehaviorSubject<StudentList[]>([]);
	selectedActivityGroups$: BehaviorSubject<StudentList[]> = new BehaviorSubject<StudentList[]>([]);

	getFlexGroups(flexPeriodId: number): void {
		this.userService
			.getStudentListsByEntityId(flexPeriodId, 'period')
			.pipe(take(1))
			.subscribe((flexPeriodStudentLists) => {
				this.existingSelectedFlexGroups$.next(flexPeriodStudentLists);
			});
	}

	getSelectionTooltipMap(
		selectedGroupsAndStudents$: Observable<(StudentList | User)[]>,
		flexPeriodId: number | null,
		activityId: number | null
	): Observable<Record<number, FlexVisibilityTooltipMessages>> {
		if (flexPeriodId === null || activityId === null) {
			return of({});
		}
		const flexPeriod$ = this.flexPeriodService.flexPeriod$(flexPeriodId);
		const activity$ = this.schoolActivityService.activity$(activityId);
		const userId$: Observable<number> = this.userService.user$.pipe(
			map((u) => {
				return u.id;
			})
		);
		return flexPeriod$.pipe(
			switchMap((flexPeriod) => {
				let isFlexRestricted = false;
				if (flexPeriod) {
					isFlexRestricted = flexPeriod.visibility !== FlexVisibility.Visible;
				}
				return iif(() => isFlexRestricted, selectedGroupsAndStudents$, of([]));
			}),
			filter((studentsAndGroups) => !!studentsAndGroups),
			distinctUntilChanged(),
			switchMap((studentsAndGroups) => {
				let groups: StudentList[] = [];
				let ownerIds: number[] = [];
				let students: User[] = [];
				const studentIdsInFlexGroups$ = this.existingSelectedFlexGroups$.pipe(
					startWith([]),
					map((studentLists) => {
						let userIds: number[] = [];
						studentLists.map((list) => {
							userIds = userIds.concat(list.user_ids);
						});
						return userIds;
					})
				);
				let uniqueOwnerIds: number[] = [];
				let obs: Observable<User[]> = of([]);
				if (studentsAndGroups) {
					groups = studentsAndGroups.filter((sg): sg is StudentList => this.userService.isGroup(sg)) as StudentList[];
					ownerIds = groups.map((group) => group.owner_id);
					students = studentsAndGroups.filter((student): student is User => !this.userService.isGroup(student)) as User[];
					uniqueOwnerIds = Array.from(new Set(ownerIds));
					if (uniqueOwnerIds.length > 0) {
						obs = this.userService.getUsersByIds(uniqueOwnerIds);
					}
				}
				// disabled group ids are whatever groups are in the activity but are not in the flex period
				// disabled student ids are whatever students are in the activity but are not in the flex period
				return combineLatest([flexPeriod$, activity$, userId$, of(groups), of(students), obs, studentIdsInFlexGroups$]);
			}),
			map(([flexPeriod, activity, userId, groups, students, owners, studentIdsInFlexGroups]) => {
				const selectionTooltipMap: Record<number, FlexVisibilityTooltipMessages> = {};
				if (groups.length === 0 && students.length === 0) {
					return selectionTooltipMap;
				}
				let disabledStudentIds: number[] = [];
				let disabledGroupIds: number[] = [];
				// every student and group in activity is disabled if flex period is restricted
				if (flexPeriod?.visibility === FlexVisibility.Restricted) {
					disabledStudentIds = activity?.visibility_student_ids || [];
					disabledGroupIds = activity?.visibility_group_ids || [];
				} else {
					// otherwise only students and groups in activity is that are not also in flex period is disabled
					if (activity?.visibility_group_ids && flexPeriod?.visibility_group_ids) {
						disabledGroupIds = activity.visibility_group_ids.filter((id: number) => !flexPeriod!.visibility_group_ids.includes(id));
					}
					if (activity?.visibility_student_ids && flexPeriod?.visibility_student_ids) {
						const studentIdsInFlex = flexPeriod!.visibility_student_ids.concat(studentIdsInFlexGroups);
						disabledStudentIds = activity.visibility_student_ids.filter((id: number) => !studentIdsInFlex.includes(id));
					}
				}
				let addedByMsg = '';
				let visibilityMsg = '';
				groups.forEach((g) => {
					addedByMsg = '';
					visibilityMsg = '';
					const owner = owners.find((owner) => owner.id === g.owner_id);
					if (owner) {
						addedByMsg = `Added by ${owner.first_name} ${owner.last_name}`;
						if (owner.id === userId) {
							addedByMsg = `Added by you`;
						}
					}
					if (disabledGroupIds.includes(g.id)) {
						visibilityMsg = `This student group doesn’t have visibility to ‘${flexPeriod!.name}’.`;
						// check for students in group that don't have flex-level visibility
						if (flexPeriod?.visibility_student_ids) {
							const visibleToCount = flexPeriod!.visibility_student_ids.filter((sid) => g.user_ids.includes(sid)).length;
							if (visibleToCount > 0) {
								let visibleToStr = `${visibleToCount}`;
								if (visibleToCount !== flexPeriod!.visibility_student_ids.length) {
									visibleToStr += `/${flexPeriod!.visibility_student_ids.length}`;
								}
								visibilityMsg = `${visibleToStr} students in the group have visibility to ‘${flexPeriod!.name}’.`;
							}
						}
					}

					if (addedByMsg || visibilityMsg) {
						selectionTooltipMap[g.id] = {
							addedByMsg: addedByMsg,
							visibilityMsg: visibilityMsg,
						};
					}
				});
				students.forEach((student) => {
					if (disabledStudentIds.includes(student.id)) {
						visibilityMsg = `This student doesn’t have visibility to ‘${flexPeriod!.name}’.`;
						selectionTooltipMap[student.id] = {
							visibilityMsg: visibilityMsg,
						};
					}
				});
				return selectionTooltipMap;
			})
		);
	}
}
